
import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, Trash2, FileText, Lock, Server, Download, ShieldCheck, Sparkles, Loader2, UploadCloud, File, CheckCircle, MessageSquare, Database, Network, AlertCircle } from 'lucide-react';
import { sendChatToBot } from '../services/n8nService'; 
import { N8N_CONFIG } from '../constants';
import { jsPDF } from "jspdf";

const KanzleiBotDemo: React.FC = () => {
  // Tabs: 'chat' | 'upload'
  const [activeMode, setActiveMode] = useState<'chat' | 'upload'>('chat');

  // --- CHAT STATE ---
  const [messages, setMessages] = useState<{role: 'user' | 'model', text: string, type?: 'text' | 'pdf_success'}[]>([
    { role: 'model', text: 'Grüß Gott. ARKORBIT Secure Bot bereit. Ich bin mit Ihrer n8n-Instanz verbunden.' }
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfStep, setPdfStep] = useState(0);
  const endRef = useRef<HTMLDivElement>(null);

  // --- UPLOAD STATE ---
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [shareLink, setShareLink] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming, isGeneratingPdf]);

  // Helper: Versucht, JSON aus dem Text zu entfernen (falls n8n Rohdaten schickt)
  const cleanMessageText = (rawText: string) => {
      if (!rawText) return "";
      if (typeof rawText !== 'string') return JSON.stringify(rawText);
      
      try {
          const trimmed = rawText.trim();
          if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
             const parsed = JSON.parse(trimmed);
             if (parsed.output) return parsed.output;
             if (parsed.text) return parsed.text;
             if (parsed.message) return parsed.message;
             if (parsed.content) return parsed.content;
          }
      } catch (e) { }
      return rawText;
  };

  const handleSend = async () => {
    if(!input.trim() || isStreaming || isGeneratingPdf) return;
    
    const userMsg = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsStreaming(true);

    try {
        // 1. Platzhalter für Bot-Antwort erstellen
        setMessages(prev => [...prev, { role: 'model', text: '' }]);
        
        // 2. Geschichte für Kontext vorbereiten
        const historyForService = messages.map(m => ({ role: m.role, text: m.text }));
        
        // 3. ECHTER CALL AN DEIN N8N
        const fullResponse = await sendChatToBot(userMsg, historyForService);
        const cleanedResponse = cleanMessageText(fullResponse);

        // 4. "Tipp-Effekt" simulieren (n8n antwortet sofort, wir machen es smooth)
        let currentText = "";
        const words = cleanedResponse.split(" ");
        
        for (let i = 0; i < words.length; i++) {
            currentText += (i > 0 ? " " : "") + words[i];
            
            // Update State in Chunks
            setMessages(prev => {
                const newArr = [...prev];
                const lastMsg = newArr[newArr.length - 1];
                if (lastMsg.role === 'model') {
                    lastMsg.text = currentText;
                }
                return newArr;
            });

            // Kleine Verzögerung für Lesbarkeit
            await new Promise(r => setTimeout(r, 30)); 
        }

        // 5. Automatische PDF Generierung erkennen
        // Wenn das Wort "Protokoll" oder "Aktenvermerk" und viel Text vorkommt -> PDF bauen
        if ((cleanedResponse.includes("Protokoll") || cleanedResponse.includes("ZUSAMMENFASSUNG")) && cleanedResponse.length > 100) {
            setTimeout(generateMockPDF, 1000);
        }

    } catch (error) {
        console.error("Chat error:", error);
        setMessages(prev => {
            const newArr = [...prev];
            // Falls der letzte Eintrag leer ist (Platzhalter), füllen wir ihn mit dem Fehler
            if (newArr[newArr.length -1].text === "") {
                newArr[newArr.length -1].text = "⚠️ Fehler: Der Kanzlei-Workflow antwortet nicht (500/502). Bitte Workflow im Monitor prüfen.";
            } else {
                 newArr.push({ role: 'model', text: "⚠️ System Error." });
            }
            return newArr;
        });
    } finally {
        setIsStreaming(false);
    }
  };

  const generateMockPDF = () => {
      setIsGeneratingPdf(true);
      setPdfStep(1);
      setTimeout(() => setPdfStep(2), 1500);
      setTimeout(() => setPdfStep(3), 3000);
      setTimeout(() => {
          setPdfStep(0);
          setIsGeneratingPdf(false);
          setMessages(prev => [...prev, { 
              role: 'model', 
              text: 'Das PDF wurde signiert und im Datentresor abgelegt.', 
              type: 'pdf_success' 
          }]);
      }, 4500);
  };

  const handleDownloadPDF = () => {
      const doc = new jsPDF();
      
      // -- HEADER --
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(1.5);
      doc.line(20, 30, 190, 30);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(15, 23, 42); 
      doc.text("ARKORBIT AI", 20, 25); // REBRANDED
      doc.setTextColor(37, 99, 235);
      doc.text("AUSTRIA", 72, 25); 

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text("BESPRECHUNGSPROTOKOLL", 190, 20, { align: "right" });
      const now = new Date();
      doc.text(`${now.toLocaleDateString('de-AT')} | ${now.toLocaleTimeString('de-AT', {hour: '2-digit', minute:'2-digit'})}`, 190, 25, { align: "right" });

      doc.setFillColor(239, 246, 255);
      doc.rect(20, 40, 170, 15, 'F');
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(0.5);
      doc.line(20, 40, 20, 55);
      
      doc.setFontSize(9);
      doc.setTextColor(30, 64, 175);
      doc.text("PROTOKOLL-HINWEIS:", 25, 46);
      doc.setFont("helvetica", "normal");
      doc.text("Der Gesprächspartner wurde zu Beginn über die digitale Aufzeichnung informiert.", 25, 51);

      let contentRaw = messages.filter(m => m.role === 'model' && !m.type).slice(-2)[0]?.text || "Inhalt...";
      let contentClean = contentRaw.replace(/\*\*/g, '').replace(/###/g, '').replace(/##/g, '');
      let yPos = 70;
      
      const splitText = doc.splitTextToSize(contentClean, 170);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(51, 65, 85);
      doc.text(splitText, 20, yPos);
      
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text("Generiert durch ARKORBIT AI Austria (n8n Core) • Nicht unterschriebenes Gedächtnisprotokoll.", 105, 280, { align: "center" });

      doc.save(`ARKORBIT_Protokoll_${now.toISOString().slice(0,10)}.pdf`);
  };

  // --- UPLOAD HANDLER ---
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleRealUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleRealUpload(e.target.files[0]);
    }
  };

  const handleRealUpload = (file: File) => {
    setUploadError(null);
    setShareLink(null);
    setSelectedFileName(file.name);
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('audio', file);
    formData.append('filename', file.name);
    formData.append('source', 'dashboard_upload');

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      setIsUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        setIsCleaningUp(true);
        try {
          const response = JSON.parse(xhr.responseText);
          if (response.shareLink || response.share_link || response.link) {
            setShareLink(response.shareLink || response.share_link || response.link);
          }
        } catch {
          // Kein JSON oder kein Link in Response – trotzdem Erfolg
        }
        setTimeout(() => {
          setIsCleaningUp(false);
          setUploadComplete(true);
        }, 2000);
      } else {
        setUploadError(`Upload fehlgeschlagen (HTTP ${xhr.status}). n8n Webhook prüfen.`);
      }
    });

    xhr.addEventListener('error', () => {
      setIsUploading(false);
      setUploadError('Verbindung zu n8n unterbrochen. VPN oder Server-Status prüfen.');
    });

    xhr.addEventListener('timeout', () => {
      setIsUploading(false);
      setUploadError('Timeout – Datei zu groß oder Server überlastet.');
    });

    xhr.timeout = 120000;
    xhr.open('POST', N8N_CONFIG.UPLOAD_AUDIO_WEBHOOK);
    xhr.send(formData);
  };

  const renderChatInterface = () => (
      <>
        <div className="flex-1 overflow-y-auto space-y-6 pr-2 scrollbar-thin scrollbar-thumb-slate-700">
            {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl p-4 ${
                        msg.role === 'user' 
                        ? 'bg-blue-600 text-white rounded-br-none' 
                        : 'bg-slate-900 border border-slate-700 text-slate-200 rounded-bl-none'
                    }`}>
                        {msg.role === 'model' && <div className="flex items-center space-x-2 mb-2 opacity-50 text-xs uppercase font-bold tracking-wider"><Bot className="w-3 h-3" /> <span>ARKORBIT AI (n8n)</span></div>}
                        
                        <div className="whitespace-pre-wrap leading-relaxed">
                            {cleanMessageText(msg.text)}
                        </div>

                        {msg.type === 'pdf_success' && (
                            <>
                                <div className="mt-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl p-4 flex items-center justify-between">
                                    <div className="flex items-center">
                                        <FileText className="w-8 h-8 text-emerald-500 mr-3" />
                                        <div>
                                            <div className="font-bold text-emerald-400 text-sm">Aktenvermerk.pdf</div>
                                            <div className="text-xs text-slate-500 flex items-center mt-1"><ShieldCheck className="w-3 h-3 mr-1" /> Signiert & Verschlüsselt</div>
                                        </div>
                                    </div>
                                    <button onClick={handleDownloadPDF} className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center">
                                        <Download className="w-4 h-4 mr-1" />
                                        Download
                                    </button>
                                </div>
                                <div className="mt-2 flex items-center justify-end text-[10px] text-slate-500">
                                    <Trash2 className="w-3 h-3 mr-1 text-slate-600" />
                                    <span>Quelldaten (Audio/Text) automatisch gelöscht.</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ))}
            
            {isStreaming && (
                <div className="flex justify-start">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl rounded-bl-none p-4 flex items-center space-x-2">
                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                        <span className="text-slate-400 text-xs font-mono animate-pulse">Sende Daten an Frankfurt Node...</span>
                    </div>
                </div>
            )}

            {isGeneratingPdf && (
                <div className="flex justify-center my-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-full px-6 py-2 flex items-center space-x-3 shadow-lg shadow-blue-900/10">
                        {pdfStep === 1 && <><Server className="w-4 h-4 text-blue-400 animate-pulse" /><span className="text-xs font-mono text-blue-300">Connecting to BMD NTCS...</span></>}
                        {pdfStep === 2 && <><FileText className="w-4 h-4 text-purple-400 animate-pulse" /><span className="text-xs font-mono text-purple-300">Layouting PDF Document...</span></>}
                        {pdfStep === 3 && <><Lock className="w-4 h-4 text-emerald-400 animate-pulse" /><span className="text-xs font-mono text-emerald-300">Signing with Handy-Signatur...</span></>}
                    </div>
                </div>
            )}

            <div ref={endRef} />
        </div>

        <div className="mt-4">
            <div className="relative">
                <input 
                    type="text" 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                    disabled={isStreaming || isGeneratingPdf}
                    placeholder={isStreaming ? "Verarbeite..." : "Nachricht an Kanzlei-Bot..."}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-4 pr-14 py-4 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
                />
                <button 
                    onClick={handleSend}
                    disabled={!input.trim() || isStreaming || isGeneratingPdf}
                    className="absolute right-2 top-2 bottom-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white w-10 rounded-lg flex items-center justify-center transition-all"
                >
                    {isStreaming ? <Sparkles className="w-5 h-5 animate-pulse" /> : <Send className="w-5 h-5" />}
                </button>
            </div>
            
            {/* CONNECTION STATUS INDICATOR */}
            <div className="flex items-center justify-center mt-2 space-x-2 text-[10px] text-slate-600">
                <div className="flex items-center">
                   <Network className="w-3 h-3 mr-1 text-emerald-500" />
                   <span>Active Pipeline: <code className="text-emerald-500/80">{N8N_CONFIG.KANZLEI_BOT_WEBHOOK}</code></span>
                </div>
                <span>•</span>
                <div className="flex items-center">
                   <Lock className="w-3 h-3 mr-1" />
                   <span>End-to-End Verschlüsselt (Frankfurt)</span>
                </div>
            </div>
        </div>
      </>
  );

  const renderUploadInterface = () => (
      <div className="flex-1 flex flex-col items-center justify-center p-6 animate-fade-in">
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,video/*,.m4a,.mp3,.wav,.ogg,.webm,.mp4,.mov"
            className="hidden"
            onChange={handleFileInputChange}
          />

          {!uploadComplete ? (
              <>
                <div 
                  className={`w-full max-w-xl h-80 border-2 border-dashed rounded-3xl flex flex-col items-center justify-center transition-all duration-300 relative overflow-hidden ${
                      uploadError
                      ? 'border-red-500/50 bg-red-500/5'
                      : dragActive 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : 'border-slate-700 bg-slate-900/50 hover:border-slate-500'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                    {/* NEXTCLOUD BRANDING BADGE */}
                    <div className="absolute top-4 right-4 bg-blue-600/20 text-blue-400 border border-blue-500/30 px-3 py-1 rounded-full text-[10px] font-bold flex items-center">
                          <Database className="w-3 h-3 mr-1" />
                          NEXTCLOUD CONNECTED
                    </div>

                    {isCleaningUp ? (
                         <div className="text-center w-64 relative z-10 animate-fade-in">
                             <Trash2 className="w-16 h-16 text-red-500 animate-bounce mx-auto mb-6" />
                             <div className="text-white font-bold mb-2">Zero-Retention Protocol</div>
                             <p className="text-slate-400 text-xs">Lösche Audio vom n8n-Server...</p>
                             <div className="mt-4 flex justify-center space-x-1">
                                 <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                                 <div className="w-2 h-2 bg-red-500 rounded-full animate-ping delay-100"></div>
                                 <div className="w-2 h-2 bg-red-500 rounded-full animate-ping delay-200"></div>
                             </div>
                         </div>
                    ) : isUploading ? (
                         <div className="text-center w-64 relative z-10">
                             <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
                             <div className="text-white font-bold mb-2 text-sm truncate max-w-xs">{selectedFileName}</div>
                             <div className="text-slate-400 text-xs mb-3">Übertrage verschlüsselt an n8n...</div>
                             <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                                 <div className="h-full bg-blue-500 transition-all duration-150" style={{ width: `${uploadProgress}%` }}></div>
                             </div>
                             <div className="text-xs text-slate-500 mt-2 font-mono">{uploadProgress}% Complete</div>
                         </div>
                    ) : uploadError ? (
                         <div className="text-center w-72 relative z-10 px-4">
                             <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
                             <div className="text-red-400 font-bold mb-2 text-sm">Upload fehlgeschlagen</div>
                             <p className="text-slate-400 text-xs mb-4">{uploadError}</p>
                             <button
                               className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-xs font-bold transition-all"
                               onClick={() => { setUploadError(null); setUploadProgress(0); }}
                             >
                               Erneut versuchen
                             </button>
                         </div>
                    ) : (
                        <div className="text-center pointer-events-none relative z-10">
                            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-700 shadow-lg">
                                 <UploadCloud className="w-10 h-10 text-slate-400" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">ARKORBIT Private Cloud</h3>
                            <p className="text-slate-400 text-sm mb-6 max-w-xs mx-auto">
                                Audio-Datei hier ablegen oder auswählen. Whisper transkribiert lokal auf dem Hetzner Server.
                            </p>
                            <div className="flex items-center justify-center space-x-4 text-xs font-bold text-slate-500">
                                <span className="flex items-center px-3 py-1 bg-slate-800 rounded-full"><Server className="w-3 h-3 mr-2 text-blue-500" /> Self-Hosted</span>
                                <span className="flex items-center px-3 py-1 bg-slate-800 rounded-full"><Lock className="w-3 h-3 mr-2 text-emerald-500" /> AES-256 Storage</span>
                            </div>
                            <button
                              className="mt-8 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-bold transition-all pointer-events-auto"
                              onClick={() => fileInputRef.current?.click()}
                            >
                                Datei sicher ablegen
                            </button>
                        </div>
                    )}
                </div>
              </>
          ) : (
              <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-3xl p-8 text-center animate-fade-in">
                   <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/30">
                       <CheckCircle className="w-10 h-10 text-emerald-500" />
                   </div>
                   <h3 className="text-2xl font-bold text-white mb-2">Verarbeitung gestartet</h3>
                   <p className="text-slate-400 mb-6">
                       n8n hat die Datei übernommen. Whisper transkribiert, Ollama erstellt die Zusammenfassung. Das Ergebnis-PDF kommt per Telegram.
                   </p>

                   {shareLink && (
                       <a
                         href={shareLink}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="block w-full bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 py-3 px-4 rounded-xl font-bold transition-all mb-4 text-sm"
                       >
                           📎 Nextcloud Share-Link öffnen
                       </a>
                   )}
                   
                   <div className="bg-black/40 rounded-xl p-4 mb-6 text-left border border-slate-800 flex items-center opacity-50">
                       <File className="w-8 h-8 text-slate-600 mr-4" />
                       <div>
                           <div className="text-slate-400 font-mono text-sm line-through">{selectedFileName || 'audio_file.m4a'}</div>
                           <div className="text-red-500 text-xs font-bold flex items-center"><Trash2 className="w-3 h-3 mr-1" /> DELETED (DSGVO)</div>
                       </div>
                   </div>

                   <button 
                       onClick={() => { setUploadComplete(false); setShareLink(null); setSelectedFileName(''); setUploadProgress(0); setActiveMode('chat'); setMessages(prev => [...prev, {role:'model', text:'Datei wurde an n8n übermittelt. Whisper transkribiert gerade. Du erhältst das PDF-Ergebnis in Kürze per Telegram.'}]); }}
                       className="w-full bg-slate-800 hover:bg-slate-700 text-white py-3 rounded-xl font-bold transition-all"
                   >
                       Zurück zum Chat
                   </button>
              </div>
          )}

          {!uploadComplete && !uploadError && (
              <div className="mt-8 flex items-start max-w-lg bg-blue-900/20 border border-blue-500/20 p-4 rounded-xl">
                   <ShieldCheck className="w-5 h-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                   <div>
                       <h4 className="text-blue-400 font-bold text-sm mb-1">DSGVO-konformer Upload</h4>
                       <p className="text-blue-200/70 text-xs leading-relaxed">
                           Audio wird direkt an die ARKORBIT-Infrastruktur (Hetzner Frankfurt) übertragen. Whisper + Ollama laufen Self-Hosted in der EU. Nach der Transkription wird die Audio-Datei automatisch gelöscht.
                       </p>
                   </div>
              </div>
          )}
      </div>
  );

  return (
    <div className="h-full flex flex-col animate-fade-in max-w-4xl mx-auto">
      <header className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white tracking-tight flex items-center">
                <Bot className="w-8 h-8 mr-3 text-blue-500" />
                ARKORBIT Assistent
            </h2>
            <p className="text-slate-400">ARKORBIT AI Austria • BMD Integration Active</p>
          </div>
          <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-800">
             <button 
                onClick={() => setActiveMode('chat')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center ${activeMode === 'chat' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
             >
                 <MessageSquare className="w-4 h-4 mr-2" /> Live Chat
             </button>
             <button 
                onClick={() => setActiveMode('upload')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center ${activeMode === 'upload' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
             >
                 <UploadCloud className="w-4 h-4 mr-2" /> Nextcloud Portal
             </button>
          </div>
      </header>

      <div className="flex-1 bg-black border border-slate-800 rounded-3xl p-4 overflow-hidden flex flex-col relative shadow-2xl">
          {activeMode === 'chat' ? renderChatInterface() : renderUploadInterface()}
          
          <div className="text-center mt-2">
              <span className="text-[10px] text-slate-600 uppercase tracking-widest flex items-center justify-center">
                  <Lock className="w-3 h-3 mr-1" /> End-to-End Verschlüsselt (Frankfurt)
              </span>
          </div>
      </div>
    </div>
  );
};

export default KanzleiBotDemo;
